import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField";
import i18n from "i18next";
import { WarehouseLocation } from "src/api/generated/erp/db/types/tables/warehouseLocation.ts";
import { PartWarehouseLocation } from "src/api/generated/erp/db/types/tables/partWarehouseLocation.ts";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { distinctBy } from "src/utils/arrayUtils.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { getPartWarehouseLocationReplenishmentTriggerLabels } from "src/api/generated/erp/db/types/enums/partWarehouseLocationReplenishmentTrigger.ts";
import { dropProps } from "src/utils/dropProps.ts";
import { getWarehouseReplenishmentMethodLabel } from "src/api/generated/erp/db/types/enums/warehouseReplenishmentMethod.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { getReplenishmentMethodOptions } from "src/components/views/erp/parts/siteParts/partWarehouseLocations/GetReplenishmentMethodOptions.tsx";
import { AavoFormContentParams } from "src/components/common/forms/AavoForm.tsx";
import {
	PartWarehouseLocationEditApi,
	PartWarehouseLocationEditApi_FormInitData,
} from "src/api/generated/erp/parts/partWarehouseLocations/api/partWarehouseLocationEditApi";
import { useEffect, useRef } from "react";
import { logError } from "src/errorHandling/errorLogging.ts";

export interface PartWarehouseLocationFormContentProps extends AavoFormContentParams<PartWarehouseLocationFormValues> {
	partId: number | null | undefined;
	partWarehouseLocationId: number | undefined;
	initData: Pick<PartWarehouseLocationEditApi_FormInitData, "givingWarehouseLocationOptions" | "siteId">;
}

export interface PartWarehouseLocationFormValues extends Omit<
	PartWarehouseLocation,
	"partWarehouseLocationId" | "partId" | "isActive" | "lastUpdated"
> {}

export const PartWarehouseLocationFormContent = ({
	partId,
	partWarehouseLocationId,
	initData: { givingWarehouseLocationOptions, siteId },
	control,
	watch,
	setValue,
	clearErrors,
	trigger,
}: PartWarehouseLocationFormContentProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const warehouseReplenishmentMethodOptions = getReplenishmentMethodOptions(tenantConfig);

	const givingSiteOptions = distinctBy(
		givingWarehouseLocationOptions,
		(warehouseLocation) => warehouseLocation.siteId,
	).map((warehouseLocation) => ({
		siteId: warehouseLocation.siteId,
		siteName: warehouseLocation.siteName,
	}));

	const replenishmentMethod = watch("replenishmentMethod");
	const givingSiteId = watch("givingSiteId");
	const replenishmentValidationInitializedRef = useRef(false);

	useEffect(() => {
		if (!replenishmentValidationInitializedRef.current) {
			replenishmentValidationInitializedRef.current = true;
			return;
		}
		const givingWarehouseTransferFieldNames = ["givingSiteId", "givingPartWarehouseLocationId"] as const;
		const replenishmentFieldNames = [
			"replenishmentMethod",
			"defaultReplenishmentQuantity",
			"replenishmentTrigger",
			...givingWarehouseTransferFieldNames,
		] as const;

		if (replenishmentMethod !== "WAREHOUSE_TRANSFER") {
			clearErrors([...givingWarehouseTransferFieldNames]);
		}
		trigger([...replenishmentFieldNames]).catch(logError);
	}, [clearErrors, replenishmentMethod, trigger]);

	return (
		<>
			<FormAsyncSelectField
				control={control}
				name={"warehouseLocationId"}
				label={i18n.t("warehouse_location")}
				getOptionKey={(option: WarehouseLocation) => option.warehouseLocationId}
				getOptionLabel={(option) => option.locationCode}
				fetchOptions={async ({ searchQuery, currentSelection }) =>
					PartWarehouseLocationEditApi.getWarehouseLocationOptions({
						partId: partId,
						siteId: siteId,
						partWarehouseLocationId: partWarehouseLocationId,
						searchQuery: searchQuery,
						currentSelection: currentSelection,
					})
				}
				rules={requireRule()}
				disabled={partWarehouseLocationId != null}
			/>
			<FormSelectField
				control={control}
				name={"replenishmentMethod"}
				label={i18n.t("replenishment_method")}
				options={warehouseReplenishmentMethodOptions}
				getOptionKey={(option) => option}
				getOptionLabel={(option) => getWarehouseReplenishmentMethodLabel(option)}
				disableClearable={false}
				onChange={(newReplenishmentMethod) => {
					if (newReplenishmentMethod !== "WAREHOUSE_TRANSFER") {
						setValue("givingPartWarehouseLocationId", null);
						clearErrors(["givingSiteId", "givingPartWarehouseLocationId"]);
					}
				}}
			/>
			<FormNumberField
				control={control}
				name={"locationQuantity"}
				label={i18n.t("quantity")}
				rules={requireRule()}
			/>
			{replenishmentMethod != null && (
				<FormNumberField
					control={control}
					name={"defaultReplenishmentQuantity"}
					label={i18n.t("default_replenishment_quantity")}
				/>
			)}
			{replenishmentMethod === "WAREHOUSE_TRANSFER" && (
				<>
					<FormSelectField
						control={control}
						name={"givingSiteId"}
						label={i18n.t("giving_site")}
						options={givingSiteOptions}
						getOptionKey={(option) => option.siteId}
						getOptionLabel={(option) => option.siteName}
						onChange={() => {
							setValue("givingPartWarehouseLocationId", null);
						}}
						startNewGridRow
						rules={requireRule()}
					/>
					<FormSelectField
						control={control}
						name={"givingPartWarehouseLocationId"}
						label={i18n.t("giving_warehouse_location")}
						options={givingWarehouseLocationOptions.filter((x) => x.siteId === givingSiteId)}
						getOptionKey={(option) => option.partWarehouseLocationId}
						getOptionLabel={(option) => option.locationCode}
						rules={requireRule()}
					/>
				</>
			)}
			{replenishmentMethod != null && (
				<FormEnumSelectField
					control={control}
					name={"replenishmentTrigger"}
					label={i18n.t("replenish_at")}
					options={dropProps(
						getPartWarehouseLocationReplenishmentTriggerLabels(),
						replenishmentMethod !== "SHOP_ORDER" ? ["BEFORE_TRANSFER_OUT"] : [],
					)}
					disableClearable
					rules={requireRule()}
				/>
			)}
			<FormCheckbox
				control={control}
				name={"isDefault"}
				label={i18n.t("part_default_warehouse_location")}
				startNewGridRow
			/>
			<FormCheckbox
				control={control}
				name={"inventoryLevelEnabled"}
				label={i18n.t("inventory_level_enabled")}
				startNewGridRow
			/>
			{replenishmentMethod != null && (
				<>
					<FormCheckbox
						control={control}
						name={"askTransferInfo"}
						label={i18n.t("ask_supply_request_info")}
						startNewGridRow
					/>
					<FormCheckbox
						control={control}
						name={"askQuantity"}
						label={i18n.t("ask_quantity")}
						startNewGridRow
					/>
				</>
			)}
			{replenishmentMethod === "WAREHOUSE_TRANSFER" && (
				<FormCheckbox
					control={control}
					name={"autoCompleteWarehouseTransfer"}
					label={i18n.t("auto_complete_warehouse_transfer")}
					startNewGridRow
				/>
			)}
		</>
	);
};
